import React from 'react';

import { ColumnDef } from '@tanstack/react-table';

import {
  SellerDealsWithCarves_user_company_deals,
  SellerDealsWithCarves_user_company_deals_bids,
} from '../../../../query/__generated__/SellerDealsWithCarves';
import { Button, WithSubData } from '../../../../ui-kit';
import {
  basisPointsToPercent,
  formatCurrency,
} from '../../../deals/DealStages/EventActionCards/utils';
import { formatCentsToDollars, toPercentage } from '../BiddingAndPricing/formatting';

export type ComparisonData = SellerDealsWithCarves_user_company_deals_bids & {
  deal: SellerDealsWithCarves_user_company_deals;
  __typename: string;
};

export const activeTradesComparisonColsOrder = [
  'deal.listing.providedName',
  'deal.listing.upb',
  'bid_basis_points',
  'carve_summary.loan_count',
  'carve_summary.current_balance_cents',
  'carve_summary.original_balance_cents',
  'carve_summary.wa_age_months',
  'carve_summary.wa_remaining_loan_terms_months',
  'carve_summary.wa_coupon_bps',
  'carve_summary.wa_borrower_credit_score',
  'carve_summary.wa_ltv_bps',
  'carve_summary.wa_dti_bps',
  'actions',
];

export const BidPXClass = {
  max: 'text-green-400',
  min: 'text-red-400',
  white: 'text-white',
};

export const getComparisonColumnDefinitions: (obj: {
  min: number;
  max: number;
}) => ColumnDef<WithSubData<ComparisonData>>[] = (bidPX) => [
  {
    header: 'Loan Pool Name',
    id: 'deal.listing.providedName',
    accessorKey: 'deal.listing.providedName',
    cell: (props) => (
      <span>{`${props.row.original.deal.listing.providedName} - BID ${props.row.original.bid_id}`}</span>
    ),
  },
  {
    header: 'UPB',
    id: 'deal.listing.upb',
    accessorKey: 'deal.listing.upb',
    cell: (props) => <span>{formatCentsToDollars(Number(props.getValue()))}</span>,
  },
  {
    header: 'Bid PX',
    id: 'bid_basis_points',
    accessorKey: 'bid_basis_points',
    cell: (props) => {
      const value = Number(props.getValue());
      const colorKey = value === bidPX.min ? 'min' : value === bidPX.max ? 'max' : 'white';
      const percent = basisPointsToPercent(value);
      const label = Number(percent).toFixed(0);
      return <span className={BidPXClass[colorKey]}>{label}%</span>;
    },
  },
  {
    accessorKey: 'carve_summary.loan_count',
    id: 'carve_summary.loan_count',
    header: '# Loans',
    meta: {
      numeric: true,
    },
  },
  {
    accessorKey: 'carve_summary.current_balance_cents',
    id: 'carve_summary.current_balance_cents',
    header: 'C Balance',
    cell: (props) => <span>{formatCurrency(Number(props.getValue()))}</span>,
    meta: {
      numeric: true,
    },
  },
  {
    accessorKey: 'carve_summary.original_balance_cents',
    id: 'carve_summary.original_balance_cents',
    header: 'O Balance',
    cell: (props) => <span>{formatCurrency(Number(props.getValue()))}</span>,
    meta: {
      numeric: true,
    },
  },
  {
    id: 'carve_summary.wa_age_months',
    accessorKey: 'carve_summary.wa_age_months',
    header: 'WALA',
    cell: (props) => <span>{String(props.getValue())}</span>,
  },
  {
    id: 'carve_summary.wa_remaining_loan_terms_months',
    accessorKey: 'carve_summary.wa_remaining_loan_terms_months',
    header: 'WAM',
    meta: {
      numeric: true,
    },
  },
  {
    header: 'GWAC',
    id: 'carve_summary.wa_coupon_bps',
    accessorKey: 'carve_summary.wa_coupon_bps',
    cell: (props) => <span>{toPercentage(Number(props.getValue()))}</span>,
  },
  {
    accessorKey: 'carve_summary.wa_borrower_credit_score',
    id: 'carve_summary.wa_borrower_credit_score',
    header: 'FICO',
    meta: {
      numeric: true,
    },
  },
  {
    accessorKey: 'carve_summary.wa_ltv_bps',
    id: 'carve_summary.wa_ltv_bps',
    header: 'LTV',
    cell: (props) => <span>{((Number(props.getValue()) ?? 0) / 100).toFixed(2)}%</span>,
    meta: {
      numeric: true,
    },
  },
  {
    accessorKey: 'carve_summary.wa_dti_bps',
    id: 'carve_summary.wa_dti_bps',
    header: 'DTI',
    cell: (props) => <span>{((Number(props.getValue()) ?? 0) / 100).toFixed(2)}%</span>,
    meta: {
      numeric: true,
    },
  },
  {
    header: '',
    id: 'actions',
    accessorKey: 'deal.listing.upb',
    cell: (props) => {
      return (
        <div className="w-[120px]">
          <Button
            type="primary"
            size="medium"
            label="Take action"
            className="hidden !min-h-[36px] !px-1 !py-0 group-hover:block"
            onClick={() => {
              window.open(
                `/marketplace/deal/${encodeURIComponent(props.row.original.deal.id)}`,
                '_blank',
              );
            }}
          >
            Take action
          </Button>
        </div>
      );
    },
    meta: {
      headerNoPill: true,
    },
  },
];
